import { useEffect, useState } from 'react'
import { SESSION_EXPIRY_HOLD_STORAGE_KEY } from 'config'
import {
  getSessionExpiryHeldByOtherTabsUntil,
  runWhenTimeReached,
} from 'shared/utils'

// The holds are waited for only until heldAtMostUntil, if it's given
const getHeldUntil = (heldAtMostUntil = Infinity) =>
  Math.min(getSessionExpiryHeldByOtherTabsUntil(), heldAtMostUntil)

// Updated when another tab changes its holds, and when the last hold runs out
export const useIsSessionExpiryHeldByAnotherTab = (
  heldAtMostUntil?: number
) => {
  const [isHeld, setIsHeld] = useState(
    () => getHeldUntil(heldAtMostUntil) > Date.now()
  )

  useEffect(() => {
    let stopWaitingForHoldEnd: (() => void) | undefined

    const update = () => {
      stopWaitingForHoldEnd?.()
      const heldUntil = getHeldUntil(heldAtMostUntil)
      const isHeldNow = heldUntil > Date.now()
      setIsHeld(isHeldNow)
      if (isHeldNow) {
        stopWaitingForHoldEnd = runWhenTimeReached(heldUntil, update)
      }
    }

    const updateOnHoldsChange = ({ key }: StorageEvent) => {
      // A null key means the whole storage was cleared
      if (key === SESSION_EXPIRY_HOLD_STORAGE_KEY || key === null) update()
    }

    update()
    window.addEventListener('storage', updateOnHoldsChange)

    return () => {
      stopWaitingForHoldEnd?.()
      window.removeEventListener('storage', updateOnHoldsChange)
    }
  }, [heldAtMostUntil])

  return isHeld
}
